import { useEffect, useLayoutEffect, useState } from 'react';

import { isMobile } from '../utils/web';

/**
 * Hook to handle mobile detection in a performant way.
 * Uses useLayoutEffect to avoid layout shifts and ensure consistent rendering on the client.
 * Uses useEffect to avoid layout shifts and ensure consistent rendering on the server.
 */
const useProperEffectDependingOnClientOrServerSideTrigger =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Hook to handle mobile detection in a performant way.
 * Uses useLayoutEffect to avoid layout shifts and ensure consistent rendering.
 *
 * Key features:
 * - Mobile-first approach (defaults to true)
 * - Synchronous update before browser paint
 * - Helps optimize Core Web Vitals (CLS)
 *
 * @returns boolean - True if the device is mobile
 *
 * @example
 * ```tsx
 * const isMobileDisplay = useIsMobile();
 *
 * return (
 *   <div className={isMobileDisplay ? 'mobile-layout' : 'desktop-layout'}>
 *     {content}
 *   </div>
 * );
 * ```
 */
export const useIsMobile = () => {
  const [isMobileDisplay, setIsMobileDisplay] = useState(true);

  useProperEffectDependingOnClientOrServerSideTrigger(() => {
    setIsMobileDisplay(isMobile());
  }, []);

  return isMobileDisplay;
};
